import { gsap, ScrollTrigger } from "@gsap";

/*
 * Instap-animaties voor de hele site, op één plek.
 * Een lijst per layout: selector, preset en eventueel stagger of delay. Een nieuwe layout doet mee door hier een regel te krijgen.
 * Bewust uitgesloten: de hero's (staan bij het laden al in beeld), culture_scroller (pint zelf) en de slides in de sliders (Swiper stuurt die aan).
 */

// Later dan eerst: een element begint pas als zijn bovenkant op 78% van het scherm zit (eerst 85%).
const TRIGGER = 0.78;
const START = `top ${TRIGGER * 100}%`;

const PRESETS = {
	"fade-up": { from: { opacity: 0, y: 28 }, to: { opacity: 1, y: 0, duration: 0.9, ease: "power3.out" } },
	fade: { from: { opacity: 0 }, to: { opacity: 1, duration: 0.9, ease: "power2.out" } },
};

const RULES = {
	".content_detail-layout": [
		[".detail-head > *", "fade-up", { stagger: 0.1 }],
		[".detail-block", "fade-up", { stagger: 0.08 }],
		[".sidebar-block", "fade-up", { stagger: 0.1 }],
		[".detail-meta", "fade-up"],
	],
	".content_story_keyfacts-layout": [
		[".story-col > *", "fade-up"],
		[".content-intro > *", "fade-up", { stagger: 0.1 }],
		[".keyfacts > li", "fade-up", { stagger: 0.1 }],
	],
	".grid_stories-layout": [
		[".filter-col", "fade-up"],
		[".story-slide", "fade-up", { stagger: 0.06 }],
	],
	".content_general-layout": [
		[".intro-content > *", "fade-up", { stagger: 0.1 }],
		[".info-box", "fade-up", { stagger: 0.08 }],
		[".image-wrapper", "fade-up"],
	],
	".content_keyfacts-layout": [
		[".content-col > *", "fade-up", { stagger: 0.1 }],
		[".keyfacts > li", "fade-up", { stagger: 0.1 }],
	],
	".content_vacancy-layout": [
		[".content-col > *", "fade-up", { stagger: 0.1 }],
		[".aside-card", "fade-up", { stagger: 0.1 }],
	],
	".vacancy_grid-layout": [
		[".heading-col > *", "fade-up"],
		[".filter-col", "fade-up"],
		[".vacancy-card", "fade-up", { stagger: 0.06 }],
	],
	".steps_accordion-layout": [[".heading-col > *", "fade-up", { stagger: 0.1 }], [".step", "fade-up", { stagger: 0.08 }]],
	".contact_cards-layout": [[".contact-card", "fade-up", { stagger: 0.1 }]],
	".faq_tabs-layout": [
		[".heading-col > *", "fade-up"],
		[".faq-wrapper", "fade-up"],
	],
	".disciplines_list-layout": [
		[".intro-content > *", "fade-up", { stagger: 0.1 }],
		[".disciplines-tabs", "fade-up", { delay: 0.1 }],
		[".panels-col", "fade-up"],
	],
	".double_cta-layout": [[".cta-card", "fade-up", { stagger: 0.18 }]],
	".swiper_steps-layout": [[".heading-col > *", "fade-up"], [".steps-swiper", "fade"], [".steps-pagination", "fade"]],
	".swiper_division-layout": [
		[".heading-col > *", "fade-up"],
		[".divisions-swiper", "fade"],
		[".divisions-pagination", "fade"],
	],
	".swiper_projects-layout": [
		[".intro-col > *", "fade-up", { stagger: 0.1 }],
		[".projects-swiper", "fade"],
	],
	".swiper_stories-layout": [
		[".heading-col > *", "fade-up"],
		[".stories-swiper", "fade"],
		[".controls-col", "fade"],
	],
	".cta_rotating-layout": [[".rotating-block", "fade-up"]],
	".keuzehulp_start-layout": [[".content-col", "fade-up"], [".image-col", "fade-up", { delay: 0.1 }]],
	".keuzehulp_confirm-layout": [[".form-col", "fade-up"], [".image-col", "fade-up", { delay: 0.1 }]],
	".keuzehulp_result-layout": [[".content-col", "fade-up"], [".image-col", "fade-up", { delay: 0.1 }]],
};

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

// Elementen die al in beeld staan blijven zichtbaar: anders knippert de pagina bij het laden.
const reveal = (targets, preset, { stagger = 0, delay = 0 } = {}) => {
	const pending = targets.filter((el) => el.getBoundingClientRect().top > window.innerHeight * TRIGGER);
	if (!pending.length) return;

	gsap.set(pending, preset.from);

	ScrollTrigger.batch(pending, {
		start: START,
		once: true,
		onEnter: (batch) => gsap.to(batch, { ...preset.to, stagger, delay, overwrite: true, clearProps: "transform,opacity" }),
	});
};

// De footer: het blauwe deel scrolt gewoon mee (alleen de juridische balk staat vast, zie footer-reveal.js), dus per element zoals de rest.
const FOOTER_RULES = [
	[".footer-panel", "fade-up"],
	[".footer-col", "fade-up", { stagger: 0.08, delay: 0.15 }],
];

const init = () => {
	// Onder prefers-reduced-motion blijft alles gewoon zichtbaar, zonder beweging.
	if (reduceMotion.matches) return;

	Object.entries(RULES).forEach(([layoutSelector, rules]) => {
		document.querySelectorAll(layoutSelector).forEach((layout) => {
			rules.forEach(([selector, preset, options]) => reveal([...layout.querySelectorAll(selector)], PRESETS[preset], options));
		});
	});

	const footer = document.querySelector(".pk-footer");
	if (footer) FOOTER_RULES.forEach(([selector, preset, options]) => reveal([...footer.querySelectorAll(selector)], PRESETS[preset], options));

	// Lettertype en afbeeldingen verschuiven de posities; de triggers opnieuw meten.
	document.fonts?.ready.then(() => ScrollTrigger.refresh());
	window.addEventListener("load", () => ScrollTrigger.refresh(), { once: true });
};

init();
